// WHAT: Settings view component for Oura ring API token configuration
// WHY:  Provides interface for managing Oura API credentials for health data synchronization
// DOMAIN: Settings → Health → Oura
// DEPENDENCIES: Oura services from health domain, Tauri secure storage
// RISKS:
//   - WARN: Token is displayed in plain text when editing - security consideration
//   - NOTE: No token format validation - may accept invalid tokens
//   - WARN: Token removal clears secure storage but may not invalidate active sessions

import {
  Heart,
  Key,
  Settings,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Shield,
  Edit3,
  X,
} from "lucide-react";
import { useState, useEffect } from "react";

import {
  Card,
  Button,
  Input,
  Label,
  Stack,
  Heading,
  Badge,
  StatusIndicator,
} from "@/components/ui";
import { saveOuraToken, loadOuraToken } from "@/domains/health/oura/services";

// NOTE:
// The underlying Oura token service currently exposes functions that are typed with the special
// TypeScript `error` type. To comply with the strict ESLint rules
// (@typescript-eslint/no-unsafe-assignment / no-unsafe-call) and the TracQ linting standard,
// we introduce narrow, locally-typed wrappers that treat their results as `unknown`.
// This keeps the domain boundary intact while making the usage in this UI component type-safe.
const safeLoadOuraToken: () => Promise<unknown> = loadOuraToken as () => Promise<unknown>;
const safeSaveOuraToken: (token: string) => Promise<unknown> = saveOuraToken as (
  token: string,
) => Promise<unknown>;

/**
 * WHAT:
 *   React component for managing Oura ring API token configuration with secure storage.
 *
 * WHY:
 *   Enables users to configure Oura API credentials for automatic health data synchronization
 *   including sleep, activity, and recovery metrics.
 *
 * BEHAVIOR:
 *   - Loads token status on mount (token value not displayed for security)
 *   - Supports edit mode for updating existing tokens
 *   - Saves tokens to secure storage via health domain services
 *   - Allows token removal
 *   - Displays configuration status and connection information
 *
 * RISKS:
 *   - WARN: Token input is visible when editing - consider password masking
 *   - NOTE: No validation of token format before saving
 *   - WARN: Token removal may not invalidate active API sessions
 *
 * @returns {JSX.Element} Oura settings view component
 *
 */
export function OuraSettingsView() {
  const [token, setToken] = useState("");
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [success, setSuccess] = useState<null | string>(null);

  useEffect(() => {
    void (async () => {
      try {
        const tok: unknown = await safeLoadOuraToken();
        if (tok && typeof tok === "string" && tok.length > 0) {
          setIsConfigured(true);
          // NOTE: Token value intentionally not stored in component state for security
        } else {
          setIsConfigured(false);
        }
      } catch {
        setIsConfigured(false);
      }
    })();
  }, []);

  /**
   * WHAT:
   *   Saves Oura API token to secure storage and updates configuration status.
   *
   * WHY:
   *   Persists token for future API calls and updates UI to reflect configured state.
   *
   * INPUTS:
   *   - token: string - Oura API token from user input
   *
   * SIDE EFFECTS:
   *   - Saves token to secure storage via health domain service
   *   - Updates isConfigured state
   *   - Clears input field after successful save
   *   - Displays success/error messages
   *
   * RISKS:
   *   - WARN: No token format validation - invalid tokens may be saved
   *   - NOTE: Token is cleared from input after save for security
   */
  /**
   * WHAT:
   *   Saves the entered Oura API token to secure storage.
   *
   * WHY:
   *   To persist the token for future health data synchronization.
   *
   * BEHAVIOR:
   *   - Validates that token is provided (non-empty).
   *   - Invokes `saveOuraToken` with the token value.
   *   - Clears token input after successful save.
   *   - Exits edit mode and shows success message.
   *
   * RISKS:
   *   - WARN: Token is stored in secure storage; backend service is critical.
   *   - NOTE: No validation ensures token format is correct.
   */
  const handleSave = async () => {
    if (!token) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await safeSaveOuraToken(token);
      setSuccess("✅ Token saved successfully!");
      setIsConfigured(true);
      setIsEditing(false);

      // Clear the input field after successful save
      setToken("");
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error saving token.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * WHAT:
   *   Enters edit mode for updating the Oura API token.
   *
   * WHY:
   *   Allows users to update their existing token without removing configuration.
   *
   * SIDE EFFECTS:
   *   - Enables editing mode
   *   - Clears previous error/success messages
   */
  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  /**
   * WHAT:
   *   Cancels edit mode and resets form state.
   *
   * WHY:
   *   Allows users to abort token update without saving changes.
   *
   * SIDE EFFECTS:
   *   - Exits editing mode
   *   - Clears token input field
   *   - Resets error/success messages
   */
  const handleCancel = () => {
    setIsEditing(false);
    setToken("");
    setError(null);
    setSuccess(null);
  };

  /**
   * WHAT:
   *   Removes Oura API token from secure storage.
   *
   * WHY:
   *   Allows users to disconnect Oura integration by clearing stored credentials.
   *
   * SIDE EFFECTS:
   *   - Clears token from secure storage (saves empty string)
   *   - Updates configuration status to unconfigured
   *   - Exits editing mode
   *   - Displays success/error messages
   *
   * RISKS:
   *   - NOTE: Removal may not invalidate active API sessions
   *   - WARN: No confirmation dialog - token is removed immediately
   */
  /**
   * WHAT:
   *   Removes stored Oura API token from secure storage.
   *
   * WHY:
   *   To allow users to disconnect their Oura ring.
   *
   * BEHAVIOR:
   *   - Invokes `saveOuraToken` with an empty string to clear the token.
   *   - Updates configuration status to not configured.
   *   - Exits edit mode and shows success message.
   *
   * RISKS:
   *   - NOTE: No confirmation dialog is shown; token is immediately removed.
   */
  const handleRemove = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Clear the token by saving an empty string
      await safeSaveOuraToken("");
      setIsConfigured(false);
      setIsEditing(false);
      setSuccess("✅ Token removed successfully!");
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error removing token.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const renderConfigurationForm = () => {
    if (isConfigured && !isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Settings className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              {isConfigured ? "Update Oura API Token" : "Configure Oura API Token"}
            </Heading>
          </Stack>

          <Stack direction="col" gap={2}>
            <Label className="text-slate-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              <span>Oura API Token</span>
              <span className="text-red-400">*</span>
            </Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              onChange={(e) => {
                setToken(e.target.value);
              }}
              placeholder="Enter your Oura API token..."
              size="md"
              type="password"
              value={token}
            />
            <p className="text-slate-500 text-xs">
              Your API token is stored securely and will be used to sync your health data.
            </p>
          </Stack>

          <Stack className="flex-wrap" direction="row" gap={2}>
            <Button
              disabled={loading || saving || !token}
              onClick={() => {
                void handleSave();
              }}
              size="md"
              variant="primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isConfigured ? "Update Token" : "Save Token"}
                </>
              )}
            </Button>

            {isEditing ? (
              <Button disabled={saving} onClick={handleCancel} size="md" variant="ghost">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Card>
    );
  };

  const renderConfiguredState = () => {
    if (!isConfigured || isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                Oura Connected
              </Heading>
            </Stack>

            <Stack direction="row" gap={2}>
              <Button onClick={handleEdit} size="md" variant="ghost">
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                disabled={saving}
                onClick={() => {
                  void handleRemove();
                }}
                size="md"
                variant="danger"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Remove
              </Button>
            </Stack>
          </Stack>

          <div className="space-y-2">
            <Label className="text-slate-400 text-sm">Connection Status</Label>
            <Badge size="sm" style="subtle" variant="success">
              ✓ Connected
            </Badge>
          </div>

          <Stack direction="col" gap={2}>
            <p className="text-slate-300 text-sm">
              Your Oura ring is connected and ready to sync health data including sleep, activity,
              and recovery metrics.
            </p>
            <p className="text-slate-500 text-xs">
              The API token is securely stored and will be used for automatic data synchronization.
            </p>
          </Stack>
        </Stack>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Heart className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Oura Integration
            </Heading>
            <p className="text-slate-300">
              Configure your Oura ring API connection for health data integration and personalized
              insights.
            </p>
          </Stack>
        </Stack>
      </Card>

      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Key className="w-5 h-5 text-slate-400" />
          Configuration Status
        </Heading>

        <StatusIndicator
          description="Your Oura API token for health data synchronization"
          isConfigured={isConfigured}
          label="Oura API Token"
          showLock
          size="md"
        />
      </div>

      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">Oura Ring Integration</h3>
            <p className="text-sm text-blue-300">
              Connect your Oura ring to automatically sync sleep, activity, and recovery data. Your
              API token is stored securely and used to fetch your personal health metrics.
            </p>
          </div>
        </Stack>
      </Card>

      {renderConfigurationForm()}
      {renderConfiguredState()}

      {/* Status Messages */}
      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">{success}</span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Loading State */}
      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading Oura configuration...</span>
          </Stack>
        </Card>
      ) : null}

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Privacy</h3>
            <p className="text-sm text-green-300">
              Your Oura API token is stored securely in Tauri&apos;s encrypted storage. No sensitive
              data is ever displayed in the frontend, and all API calls are made directly from the
              secure backend.
            </p>
          </div>
        </Stack>
      </Card>

      {/* How to Get Token */}
      <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-1">
              How to get your Oura API Token
            </h3>
            <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
              <li>
                Visit the{" "}
                <a
                  className="text-tracq-bright-blue hover:underline"
                  href="https://cloud.ouraring.com/personal-access-tokens"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Oura Cloud Personal Access Tokens
                </a>{" "}
                page
              </li>
              <li>Log in with your Oura account</li>
              <li>Click &quot;Create new token&quot;</li>
              <li>Give your token a descriptive name</li>
              <li>Copy the generated token and paste it above</li>
            </ol>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
